// hotel/F&B/components/ClosePreviousShift.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Tabs,
  Tab,
  Avatar,
  Stack
} from '@mui/material';
import {
  CalendarToday,
  CheckCircle,
  Close,
  CreditCard,
  History,
  Money,
  Payment,
  Print,
  Refresh,
  WarningAmber,
  CardGiftcard,
  AccountBalance,
  Restaurant,
  LocalBar,
  RoomService,
  TakeoutDining,
  DeliveryDining,
  AttachMoney,
  Receipt
} from '@mui/icons-material';
import axios from 'axios';
import { styled } from '@mui/material/styles';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Styled Components
const ShiftCard = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  border: selected ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
  backgroundColor: selected ? 'rgba(25, 118, 210, 0.05)' : 'white',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4]
  }
}));

const ClosePreviousShift = ({ open, onClose, onShiftClosed }) => {
  const [loading, setLoading] = useState(false);
  const [unclosedShifts, setUnclosedShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedShiftDetails, setSelectedShiftDetails] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedUnpaidOrder, setSelectedUnpaidOrder] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('Cash');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [closingShift, setClosingShift] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmCloseText, setConfirmCloseText] = useState('');

  const getHotelCode = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';
    } catch (error) {
      return '';
    }
  };

  const getCurrentCashierId = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.staff_id || storedProfile.id || null;
    } catch (error) {
      return null;
    }
  };

  const getCurrentCashierName = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.first_name || storedProfile.name || 'Cashier';
    } catch (error) {
      return 'Cashier';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 2 }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to check if shift can be closed
  const canCloseSelectedShift = () => {
    if (!selectedShiftDetails) return false;
    return selectedShiftDetails.can_close === true;
  };

  const loadUnclosedShifts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/hotel/F&B/close_previous_shift.php`, {
        params: {
          action: 'get_unclosed_shifts',
          hotel_code: getHotelCode()
        }
      });
      
      if (response.data.success) {
        setUnclosedShifts(response.data.unclosed_shifts);
        if (response.data.unclosed_shifts.length > 0 && !selectedShift) {
          setSelectedShift(response.data.unclosed_shifts[0]);
          loadShiftDetails(response.data.unclosed_shifts[0].shift_date);
        }
      }
    } catch (error) {
      console.error('Error loading unclosed shifts:', error);
      showSnackbar('Failed to load unclosed shifts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadShiftDetails = async (shiftDate) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/hotel/F&B/close_previous_shift.php`, {
        params: {
          action: 'get_shift_details',
          hotel_code: getHotelCode(),
          shift_date: shiftDate
        }
      });
      
      if (response.data.success) {
        setSelectedShiftDetails(response.data);
      }
    } catch (error) {
      console.error('Error loading shift details:', error);
      showSnackbar('Failed to load shift details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleShiftSelect = (shift) => {
    setSelectedShift(shift);
    loadShiftDetails(shift.shift_date);
  };

  const handleRecordPayment = async () => {
    if (!selectedUnpaidOrder) return;
    
    setProcessingPayment(true);
    try {
      // Determine the amount - for complimentary, it should be 0
      const amount = selectedPaymentMethod === 'Complimentary' ? 0 : selectedUnpaidOrder.final_amount;
      
      const response = await axios.post(`${API_BASE_URL}/hotel/F&B/close_previous_shift.php?action=record_payment`, {
        hotel_code: getHotelCode(),
        shift_date: selectedShift.shift_date,
        cashier_id: getCurrentCashierId(),
        cashier_name: getCurrentCashierName(),
        order_id: selectedUnpaidOrder.id,
        payment_method: selectedPaymentMethod,
        amount: amount,
        // Include shift_id if available from the order
        shift_id: selectedUnpaidOrder.shift_id || null,
        payment_date: `${selectedShift.shift_date} ${new Date().toLocaleTimeString()}`
      });
      
      if (response.data.success) {
        showSnackbar(`Payment recorded successfully for order #${selectedUnpaidOrder.order_number}!`, 'success');
        setShowPaymentDialog(false);
        setSelectedUnpaidOrder(null);
        setSelectedPaymentMethod('Cash');
        // Refresh shift details
        loadShiftDetails(selectedShift.shift_date);
        // Refresh shifts list
        loadUnclosedShifts();
      } else {
        showSnackbar(response.data.error || 'Failed to record payment', 'error');
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      showSnackbar(error.response?.data?.error || 'Failed to record payment', 'error');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCloseShift = async () => {
    if (confirmCloseText !== 'CLOSE SHIFT') {
      showSnackbar('Please type "CLOSE SHIFT" to confirm', 'error');
      return;
    }
    
    setClosingShift(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/hotel/F&B/close_previous_shift.php?action=close_shift`, {
        hotel_code: getHotelCode(),
        shift_date: selectedShift.shift_date,
        cashier_id: getCurrentCashierId(),
        cashier_name: getCurrentCashierName()
      });
      
      if (response.data.success) {
        showSnackbar(response.data.message, 'success');
        setConfirmCloseText('');
        // Refresh the list
        await loadUnclosedShifts();
        if (onShiftClosed) {
          onShiftClosed();
        }
        // Auto close if no more shifts
        if (unclosedShifts.length === 1) {
          onClose();
        }
      } else {
        showSnackbar(response.data.error || 'Failed to close shift', 'error');
      }
    } catch (error) {
      console.error('Error closing shift:', error);
      showSnackbar(error.response?.data?.error || 'Failed to close shift', 'error');
    } finally {
      setClosingShift(false);
    }
  };

  const handlePrintReceipt = (order) => {
    const paymentMethod = order.payment_method || 'Cash';
    const hotelName = 'Frankville Hotels';
    const shiftId = order.shift_id || 'N/A';
    const shiftDate = order.shift_date || selectedShift?.shift_date || '';
    
    const printWindow = window.open('', '_blank', 'width=450,height=700');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${order.order_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.5;
            padding: 15px;
            width: 300px;
            margin: 0 auto;
          }
          .header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 10px; margin-bottom: 12px; }
          .hotel-name { font-size: 18px; font-weight: bold; }
          .receipt-type { font-size: 14px; font-weight: bold; color: #2E7D32; margin: 8px 0; }
          .reprint-badge { background-color: #FF9800; color: white; padding: 4px 10px; border-radius: 4px; font-size: 10px; display: inline-block; }
          .order-info { margin-bottom: 12px; border-bottom: 1px solid #ccc; padding-bottom: 8px; }
          .order-info-row { display: flex; justify-content: space-between; margin-bottom: 4px; }
          .items-header { display: flex; justify-content: space-between; font-weight: bold; border-bottom: 1px solid #000; padding: 5px 0; }
          .item-row { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px dotted #eee; }
          .breakdown { border-top: 1px solid #ccc; padding-top: 8px; margin-top: 8px; }
          .breakdown-row { display: flex; justify-content: space-between; margin-bottom: 3px; }
          .total { border-top: 2px solid #000; padding-top: 8px; margin-top: 5px; font-weight: bold; font-size: 14px; display: flex; justify-content: space-between; }
          .payment-method { text-align: center; margin: 15px 0; padding: 10px; background-color: #E8F5E9; border-radius: 8px; font-weight: bold; }
          .shift-info { font-size: 9px; text-align: center; color: #666; margin-top: 8px; padding-top: 5px; border-top: 1px dotted #ccc; }
          .footer { text-align: center; border-top: 2px dashed #000; padding-top: 10px; margin-top: 12px; font-size: 9px; }
          .backdate-note { color: #FF9800; font-size: 10px; text-align: center; margin-top: 8px; }
          .thank-you { text-align: center; margin: 12px 0; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="hotel-name">${hotelName}</div>
          <div class="receipt-type">CASHIER RECEIPT (BACKDATED)</div>
          <div class="reprint-badge">*** BACKDATED RECEIPT ***</div>
        </div>
        
        <div class="order-info">
          <div class="order-info-row"><span>Order #:</span><span>${order.order_number}</span></div>
          <div class="order-info-row"><span>Date:</span><span>${formatDateTime(order.created_at)}</span></div>
          <div class="order-info-row"><span>Order Type:</span><span>${order.order_type}</span></div>
          ${order.table_number ? `<div class="order-info-row"><span>Table:</span><span>${order.table_number}</span></div>` : ''}
          ${order.guest_name ? `<div class="order-info-row"><span>Guest:</span><span>${order.guest_name}</span></div>` : ''}
        </div>
        
        <div class="items-header">
          <span>Item</span>
          <span>Qty</span>
          <span>Price</span>
          <span>Total</span>
        </div>
        ${order.items?.map(item => `
          <div class="item-row">
            <span>${item.item_name}</span>
            <span>${item.quantity}</span>
            <span>₦${parseFloat(item.unit_price).toFixed(2)}</span>
            <span>₦${(item.quantity * item.unit_price).toFixed(2)}</span>
          </div>
        `).join('')}
        
        <div class="breakdown">
          <div class="breakdown-row"><span>Subtotal:</span><span>₦${parseFloat(order.total_amount).toFixed(2)}</span></div>
          ${order.tax_amount > 0 ? `<div class="breakdown-row"><span>Tax:</span><span>₦${parseFloat(order.tax_amount).toFixed(2)}</span></div>` : ''}
          ${order.service_charge > 0 ? `<div class="breakdown-row"><span>Service Charge:</span><span>₦${parseFloat(order.service_charge).toFixed(2)}</span></div>` : ''}
        </div>
        
        <div class="total">
          <span>TOTAL:</span>
          <span>₦${parseFloat(order.final_amount).toFixed(2)}</span>
        </div>
        
        <div class="payment-method">
          PAYMENT METHOD: ${paymentMethod.toUpperCase()}
        </div>
        
        <div class="shift-info">
          Shift ID: ${shiftId} | Shift Date: ${shiftDate}
        </div>
        
        <div class="backdate-note">
          ⚠️ This payment was recorded on ${new Date().toLocaleDateString()} for a previous day's transaction.
        </div>
        
        <div class="thank-you">
          Thank you for choosing ${hotelName}!
        </div>
        
        <div class="footer">
          This is a computer generated receipt<br>
          Please retain for your records<br>
          ${new Date().toLocaleString()}
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            }, 300);
          }
        <\/script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    if (open) {
      loadUnclosedShifts();
    }
  }, [open]);

  const getOrderTypeIcon = (orderType) => {
    switch (orderType) {
      case 'dine_in': return <LocalBar fontSize="small" />;
      case 'Lounge': return <LocalBar fontSize="small" />;
      case 'room_service': return <RoomService fontSize="small" />;
      case 'takeaway': return <TakeoutDining fontSize="small" />;
      case 'delivery': return <DeliveryDining fontSize="small" />;
      default: return <Restaurant fontSize="small" />;
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'Cash': return <Money fontSize="small" />;
      case 'POS Terminal': return <CreditCard fontSize="small" />;
      case 'Bank Transfer': return <AccountBalance fontSize="small" />;
      case 'Complimentary': return <CardGiftcard fontSize="small" />;
      default: return <Payment fontSize="small" />;
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #0c0421 0%, #0a041f 100%)', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <History />
              <Typography variant="h5" fontWeight="bold">Close Previous Shift</Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress size={50} />
            </Box>
          ) : unclosedShifts.length === 0 ? (
            <Alert severity="success" sx={{ m: 2 }}>
              ✅ All shifts are properly closed! No pending shifts to close.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {/* Left Panel - List of unclosed shifts */}
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Unclosed Shifts ({unclosedShifts.length})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {unclosedShifts.map((shift) => (
                    <ShiftCard
                      key={shift.shift_date}
                      selected={selectedShift?.shift_date === shift.shift_date}
                      onClick={() => handleShiftSelect(shift)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {formatDate(shift.shift_date)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {shift.unpaid_orders_count} unpaid orders • {shift.payments_count} payments
                          </Typography>
                          {shift.pending_orders_count > 0 && (
                            <Typography variant="caption" color="error" display="block">
                              ⚠️ {shift.pending_orders_count} pending orders
                            </Typography>
                          )}
                        </Box>
                        <Chip 
                          size="small" 
                          label={shift.can_be_closed ? "Ready to Close" : "Cannot Close"} 
                          color={shift.can_be_closed ? "success" : "error"} 
                          icon={shift.can_be_closed ? <CheckCircle /> : <WarningAmber />}
                        />
                      </Box>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="primary" fontWeight="bold">
                          Total: {formatCurrency(shift.payments_total)}
                        </Typography>
                      </Box>
                    </ShiftCard>
                  ))}
                </Paper>
              </Grid>
              
              {/* Right Panel - Shift Details */}
              <Grid item xs={12} md={8}>
                {selectedShiftDetails ? (
                  <Box>
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                          Shift Details - {formatDate(selectedShift.shift_date)}
                        </Typography>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          startIcon={<Refresh />}
                          onClick={() => loadShiftDetails(selectedShift.shift_date)}
                        >
                          Refresh
                        </Button>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
                            <Typography variant="caption" color="textSecondary">Total Orders</Typography>
                            <Typography variant="h6">{selectedShiftDetails.payments_count}</Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
                            <Typography variant="caption" color="textSecondary">Total Revenue</Typography>
                            <Typography variant="h6" color="success.main">{formatCurrency(selectedShiftDetails.payments_total)}</Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
                            <Typography variant="caption" color="textSecondary">Unpaid Orders</Typography>
                            <Typography variant="h6" color={selectedShiftDetails.unpaid_orders_count > 0 ? 'error' : 'success'}>
                              {selectedShiftDetails.unpaid_orders_count}
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
                            <Typography variant="caption" color="textSecondary">Pending Orders</Typography>
                            <Typography variant="h6" color={selectedShiftDetails.pending_orders_count > 0 ? 'warning' : 'success'}>
                              {selectedShiftDetails.pending_orders_count || 0}
                            </Typography>
                          </Card>
                        </Grid>
                      </Grid>
                    </Paper>
                    
                    {/* Tabs for different sections */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                        <Tab label={`Payments (${selectedShiftDetails.payments?.length || 0})`} />
                        <Tab label={`Unpaid Orders (${selectedShiftDetails.unpaid_orders_count || 0})`} />
                        <Tab label={`Pending Orders (${selectedShiftDetails.pending_orders_count || 0})`} />
                        <Tab label={`Waiters (${selectedShiftDetails.waiters?.length || 0})`} />
                      </Tabs>
                    </Box>
                    
                    {/* Payments Tab - Updated to show shift info */}
                    {activeTab === 0 && (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                              <TableCell><strong>Payment #</strong></TableCell>
                              <TableCell><strong>Order #</strong></TableCell>
                              <TableCell><strong>Payment Method</strong></TableCell>
                              <TableCell align="right"><strong>Amount</strong></TableCell>
                              <TableCell><strong>Cashier</strong></TableCell>
                              <TableCell><strong>Shift ID</strong></TableCell>
                              <TableCell><strong>Time</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedShiftDetails.payments?.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={7} align="center">No payments recorded for this day</TableCell>
                              </TableRow>
                            ) : (
                              selectedShiftDetails.payments.map((payment) => (
                                <TableRow key={payment.id}>
                                  <TableCell>{payment.payment_number}</TableCell>
                                  <TableCell>{payment.order_number}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      size="small" 
                                      icon={getPaymentMethodIcon(payment.payment_method)}
                                      label={payment.payment_method}
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right" fontWeight="bold">{formatCurrency(payment.amount)}</TableCell>
                                  <TableCell>{payment.created_by_name || payment.created_by}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      size="small" 
                                      label={`#${payment.shift_id || 'N/A'}`}
                                      variant="outlined"
                                      color="info"
                                    />
                                  </TableCell>
                                  <TableCell>{formatDateTime(payment.payment_date)}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    
                    {/* Unpaid Orders Tab */}
                    {activeTab === 1 && (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                              <TableCell><strong>Order #</strong></TableCell>
                              <TableCell><strong>Shift ID</strong></TableCell>
                              <TableCell><strong>Type</strong></TableCell>
                              <TableCell><strong>Guest</strong></TableCell>
                              <TableCell><strong>Items</strong></TableCell>
                              <TableCell align="right"><strong>Amount</strong></TableCell>
                              <TableCell><strong>Waiter</strong></TableCell>
                              <TableCell align="center"><strong>Action</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedShiftDetails.unpaid_orders?.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={8} align="center">
                                  <Alert severity="success" icon={<CheckCircle />}>
                                    No unpaid orders! All payments are recorded.
                                  </Alert>
                                </TableCell>
                              </TableRow>
                            ) : (
                              selectedShiftDetails.unpaid_orders.map((order) => (
                                <TableRow key={order.id}>
                                  <TableCell>{order.order_number}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      size="small" 
                                      label={`#${order.shift_id || 'N/A'}`}
                                      variant="outlined"
                                      color="info"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      size="small" 
                                      icon={getOrderTypeIcon(order.order_type)}
                                      label={order.order_type}
                                    />
                                  </TableCell>
                                  <TableCell>{order.guest_name}</TableCell>
                                  <TableCell>
                                    {order.items?.slice(0, 2).map((item, i) => (
                                      <Typography key={i} variant="caption" display="block">
                                        {item.quantity}x {item.item_name}
                                      </Typography>
                                    ))}
                                    {order.items?.length > 2 && (
                                      <Typography variant="caption" color="textSecondary">
                                        +{order.items.length - 2} more
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right" fontWeight="bold" color="error.main">
                                    {formatCurrency(order.final_amount)}
                                  </TableCell>
                                  <TableCell>{order.created_by_name}</TableCell>
                                  <TableCell align="center">
                                    <Stack direction="row" spacing={1}>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        startIcon={<Print />}
                                        onClick={() => handlePrintReceipt(order)}
                                      >
                                        Print
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        startIcon={<Payment />}
                                        onClick={() => {
                                          setSelectedUnpaidOrder(order);
                                          setShowPaymentDialog(true);
                                        }}
                                      >
                                        Record Payment
                                      </Button>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    
                    {/* Pending Orders Tab */}
                    {activeTab === 2 && (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                              <TableCell><strong>Order #</strong></TableCell>
                              <TableCell><strong>Shift ID</strong></TableCell>
                              <TableCell><strong>Status</strong></TableCell>
                              <TableCell><strong>Type</strong></TableCell>
                              <TableCell><strong>Guest</strong></TableCell>
                              <TableCell><strong>Items</strong></TableCell>
                              <TableCell align="right"><strong>Amount</strong></TableCell>
                              <TableCell><strong>Waiter</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedShiftDetails.pending_orders?.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={8} align="center">
                                  <Alert severity="success" icon={<CheckCircle />}>
                                    No pending orders!
                                  </Alert>
                                </TableCell>
                              </TableRow>
                            ) : (
                              selectedShiftDetails.pending_orders.map((order) => (
                                <TableRow key={order.id}>
                                  <TableCell>{order.order_number}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      size="small" 
                                      label={`#${order.shift_id || 'N/A'}`}
                                      variant="outlined"
                                      color="info"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      size="small" 
                                      label={order.status}
                                      color={order.status === 'pending' ? 'warning' : 'info'}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      size="small" 
                                      icon={getOrderTypeIcon(order.order_type)}
                                      label={order.order_type}
                                    />
                                  </TableCell>
                                  <TableCell>{order.guest_name}</TableCell>
                                  <TableCell>
                                    {order.items?.slice(0, 2).map((item, i) => (
                                      <Typography key={i} variant="caption" display="block">
                                        {item.quantity}x {item.item_name}
                                      </Typography>
                                    ))}
                                    {order.items?.length > 2 && (
                                      <Typography variant="caption" color="textSecondary">
                                        +{order.items.length - 2} more
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">{formatCurrency(order.final_amount)}</TableCell>
                                  <TableCell>{order.created_by_name}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    
                    {/* Waiters Tab */}
                    {activeTab === 3 && (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                              <TableCell><strong>Waiter Name</strong></TableCell>
                              <TableCell><strong>Waiter ID</strong></TableCell>
                              <TableCell align="center"><strong>Orders</strong></TableCell>
                              <TableCell align="right"><strong>Revenue</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedShiftDetails.waiters?.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} align="center">No waiters recorded for this day</TableCell>
                              </TableRow>
                            ) : (
                              selectedShiftDetails.waiters.map((waiter) => (
                                <TableRow key={waiter.waiter_id}>
                                  <TableCell>{waiter.waiter_name}</TableCell>
                                  <TableCell>{waiter.waiter_id}</TableCell>
                                  <TableCell align="center">{waiter.order_count}</TableCell>
                                  <TableCell align="right" fontWeight="bold">{formatCurrency(waiter.total_revenue)}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    
                    {/* Close Shift Section */}
                    <Paper sx={{ mt: 3, p: 3, borderRadius: 2, bgcolor: canCloseSelectedShift() ? '#FFF8E1' : '#FFEBEE' }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom color={canCloseSelectedShift() ? "warning.main" : "error.main"}>
                        {canCloseSelectedShift() ? '⚠️ Close Shift' : '❌ Cannot Close Shift'}
                      </Typography>
                      
                      {/* Show error message if shift cannot be closed */}
                      {!canCloseSelectedShift() && selectedShiftDetails?.cannot_close_message && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>{selectedShiftDetails.cannot_close_message}</strong>
                          </Typography>
                          {selectedShiftDetails.pending_orders_count > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" display="block">
                                📋 Pending Orders ({selectedShiftDetails.pending_orders_count}): Please ask waiters to complete these orders before closing.
                              </Typography>
                            </Box>
                          )}
                          {selectedShiftDetails.unpaid_orders_count > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" display="block">
                                💰 Unpaid Orders ({selectedShiftDetails.unpaid_orders_count}): Please record payments for these orders first.
                              </Typography>
                            </Box>
                          )}
                        </Alert>
                      )}
                      
                      {canCloseSelectedShift() && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Important Notes:</strong>
                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                              <li>All payments will be saved with the original date ({formatDate(selectedShift.shift_date)})</li>
                              <li>All cashiers and waiters will be credited for their transactions</li>
                              <li>This action cannot be undone</li>
                              {selectedShiftDetails.unpaid_orders_count > 0 && (
                                <li style={{ color: '#d32f2f' }}>
                                  ⚠️ {selectedShiftDetails.unpaid_orders_count} unpaid order(s) remaining - they will remain unpaid in the system
                                </li>
                              )}
                            </ul>
                          </Typography>
                        </Alert>
                      )}
                      
                      <TextField
                        fullWidth
                        size="small"
                        label="Type 'CLOSE SHIFT' to confirm"
                        value={confirmCloseText}
                        onChange={(e) => setConfirmCloseText(e.target.value)}
                        error={confirmCloseText !== '' && confirmCloseText !== 'CLOSE SHIFT'}
                        helperText={confirmCloseText !== '' && confirmCloseText !== 'CLOSE SHIFT' ? 'Please type CLOSE SHIFT exactly' : ''}
                        sx={{ mb: 2 }}
                        disabled={!canCloseSelectedShift()}
                      />
                      
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        startIcon={closingShift ? <CircularProgress size={20} /> : <CheckCircle />}
                        onClick={handleCloseShift}
                        disabled={!canCloseSelectedShift() || closingShift || confirmCloseText !== 'CLOSE SHIFT'}
                        sx={{ 
                          py: 1.5,
                          background: canCloseSelectedShift() 
                            ? 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)'
                            : 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)',
                          '&:hover': { 
                            transform: canCloseSelectedShift() ? 'scale(1.02)' : 'none',
                            background: canCloseSelectedShift() 
                              ? 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)'
                              : 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)'
                          }
                        }}
                      >
                        {closingShift ? 'Closing Shift...' : 'Confirm Close Shift'}
                      </Button>
                    </Paper>
                  </Box>
                ) : (
                  <Paper sx={{ p: 5, textAlign: 'center' }}>
                    <Typography color="textSecondary">Select a shift to view details</Typography>
                  </Paper>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} variant="outlined">Close</Button>
          <Button 
            onClick={loadUnclosedShifts} 
            variant="contained" 
            startIcon={<Refresh />}
            disabled={loading}
          >
            Refresh
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Payment Recording Dialog - Updated to show shift info */}
      <Dialog open={showPaymentDialog} onClose={() => setShowPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Record Payment - Order #{selectedUnpaidOrder?.order_number}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Order Details:</strong><br />
                Amount: {formatCurrency(selectedUnpaidOrder?.final_amount)}<br />
                Date of original order: {formatDate(selectedUnpaidOrder?.created_at)}<br />
                Shift ID: {selectedUnpaidOrder?.shift_id || 'N/A'}<br />
                <em style={{ fontSize: '12px' }}>Payment will be backdated to {formatDate(selectedShift?.shift_date)}</em>
              </Typography>
            </Alert>
            
            <Typography variant="subtitle2" gutterBottom>Select Payment Method</Typography>
            
            <RadioGroup
              value={selectedPaymentMethod}
              onChange={(e) => setSelectedPaymentMethod(e.target.value)}
            >
              <FormControlLabel value="Cash" control={<Radio />} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Money /> Cash
                </Box>
              } />
              <FormControlLabel value="POS Terminal" control={<Radio />} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditCard /> POS Terminal
                </Box>
              } />
              <FormControlLabel value="Bank Transfer" control={<Radio />} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalance /> Bank Transfer
                </Box>
              } />
              <FormControlLabel value="Complimentary" control={<Radio />} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CardGiftcard sx={{ color: '#FF9800' }} /> Complimentary (No Payment)
                </Box>
              } />
            </RadioGroup>

            {selectedPaymentMethod === 'Complimentary' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Complimentary Order</strong> - No payment will be recorded. 
                  The amount will be set to ₦0.00 in the payments table.
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleRecordPayment}
            disabled={processingPayment}
            startIcon={processingPayment ? <CircularProgress size={20} /> : <Payment />}
          >
            {processingPayment ? 'Processing...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ClosePreviousShift;